// @flow

import React from 'react';
import {
  Button,
  makeStyles,
  Typography,
  Checkbox,
  Tooltip,
  IconButton,
  Popover,
} from '@material-ui/core';
import {Warning, ExpandMore, Close} from '@material-ui/icons';
import {colors} from '../../../../../../assets/styles/colors';

import FileTypeIcons from '../../../../../../elements/FileTypeIcons';

import {timeAgo} from '../../../../../../utils/date';
import {
  getConversationDisplayName,
  isValidDisplayIdentifierOrPhone,
  useConversationActions,
} from '../../../utils/conversation';

import type {Theme} from '@material-ui/core';
import type {
  Conversation,
  ChatServiceType,
  StoreDevice,
} from '../../../../../../entities';

import {formatPhoneNumber} from '../../../../../../utils/phone';
import {getMultichatServiceIcon} from '../../../../utils/service-icon';
import EmergencyIcon from '../../../../../../elements/icons/EmergencyIcon';
import PinIcon from '../../../../../../elements/icons/PinIcon';
import TiendadaTooltip from '../../../../../../elements/tiendada-tooltip';
import {useState} from 'react';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';

type Props = {
  current: boolean,
  conversation: Conversation,
  onClick: (c: Conversation) => void,
  onToggleSelect: (c: Conversation) => void,
  selected: boolean,
  currentDevice: StoreDevice,
};

const useStyles = props =>
  makeStyles((theme: Theme) => ({
    previewWrap: {
      padding: 0,
      textAlign: 'left',
      fontWeight: 400,
      borderRadius: 0,
      width: '100%',
      position: 'relative',
      '&:hover $moreOptionsButton': {
        opacity: 1,
      },
    },
    preview: {
      textTransform: 'none',
      borderBottom: `1px solid ${colors.grey300}`,
      borderLeft: `4px solid ${props.current ? colors.purple : 'transparent'}`,
      padding: theme.spacing(1.5, 2),
      backgroundColor: props.current ? colors.grey200 : '#FFFFFF',
      width: '100%',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',

      '&[data-checked="true"]': {
        paddingLeft: 0,
      },

      '&:hover *[data-checkbox]:not([data-checked="true"])': {
        width: '50%',
      },
    },
    checkboxContainer: {
      position: 'absolute',
      left: 0,
      height: '100%',
      top: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      background: 'linear-gradient(90deg, #f4f4f6 10%, transparent 100%)',
      zIndex: 1,
      overflow: 'hidden',
      transition: 'width .2s ease-in-out',
      width: 0,
      pointerEvents: 'none',
      flexShrink: 0,

      '&[data-checked="true"]': {
        width: 'auto',
        position: 'relative',
        background: 'transparent',
      },
    },
    checkbox: {
      pointerEvents: 'all',
    },
    header: {
      display: 'flex',
      // alignItems: 'center',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
    contactInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    contactName: {
      color: colors.grey900,
      fontSize: 14,
      fontWeight: 600,
    },
    time: {
      color: colors.grey600,
      fontSize: 10,
      // maxWidth: '33%',
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'right',
    },
    message: {
      display: '-webkit-box',
      overflow: 'hidden',
      '-webkit-line-clamp': 1,
      '-webkit-box-orient': 'vertical',
      margin: 0,
      color: colors.grey800,
      fontSize: 14,
      lineHeight: '20px',
    },
    messageContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '.25rem',
      position: 'relative',
    },
    tagList: {
      display: 'flex',
      gap: '.5rem',
      width: '100%',
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        width: '5px',
        height: '5px',
      },

      '&::-webkit-scrollbar-track': {
        background: colors.grey200,
        borderRadius: '5px',
      },

      '&::-webkit-scrollbar-thumb': {
        background: colors.grey400,
        borderRadius: '5px',
      },
    },
    content: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '.25rem',
      flexGrow: 1,
      width: '100%',
    },
    unreadBulled: {
      position: 'absolute',
      top: '50%',
      right: '3px',
      transform: 'translateY(-50%)',
      width: '.5rem',
      height: '.5rem',
      borderRadius: '50%',
      backgroundColor: colors.purple,
    },
    tag: {
      padding: '.125rem .45rem',
      borderRadius: '.5rem',
      backgroundColor: `${colors.purple}1A`,
      whiteSpace: 'nowrap',
      color: colors.purple,
      fontWeight: 600,
      fontSize: 10,
    },
    messageIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem',
    },
    messageMultimedia: {
      display: 'flex',
      gap: '.5rem',
      alignItems: 'center',
    },
    messageMultimediaLabel: {
      color: colors.grey800,
      fontSize: '.75rem',
    },
    previewServiceIcon: {
      width: '1.25rem',
      height: '1.25rem',
      flexShrink: 0,
      borderRadius: '50%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
    },

    textTooltip: {
      color: colors.grey700,
      fontSize: '.8rem',
      fontWeight: 500,
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    serviceLabel: {
      display: 'flex',
      alignItems: 'center',
      width: '50%',
      gap: '.5rem',
      overflow: 'hidden',
    },
    moreOptionsButton: {
      position: 'absolute',
      top: 0,
      right: 0,
      opacity: 0,
      background: 'rgba(255, 255, 255, 0.5)',
      backdropFilter: 'blur(1rem)',
      zIndex: 2,
    },
    conversationInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      maxWidth: '33%',
      position: 'relative',
    },
    conversationState: {
      position: 'absolute',
      top: '1rem',
      right: 0,
      color: '#fbaf19',
      fontSize: '1rem',
      textAlign: 'right',
    },
    conversationStateBulled: {
      position: 'absolute',
      top: 0,
      right: 0,
      color: '#fbaf19',
      fontSize: '1rem',
      textAlign: 'right',
    },
    conversationFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      width: '100%',
    },
    icon: {
      color: colors.grey700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    enabled: {
      color: colors.success,
    },
    requiresAssistance: {
      display: 'flex',
      alignItems: 'center',
      gap: '.25rem',
      color: colors.danger,
      fontSize: '.725rem',
      fontWeight: 500,
      backgroundColor: `${colors.danger}1A`,
      padding: '.125rem .5rem',
      borderRadius: '.75rem',
    },
    popoverContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '.125rem',
      padding: '.5rem',
      borderRadius: '.75rem',
      backdropFilter: 'blur(1rem)',
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
  }));

function getLabelByMessageType(type: string) {
  switch (type) {
    case 'document':
    case 'DOCUMENT':
      return 'Documento';

    case 'image':
    case 'IMAGE':
      return 'Imagen';

    case 'video':
    case 'VIDEO':
      return 'Video';

    case 'audio':
    case 'AUDIO':
      return 'Audio';

    case 'TEMPLATE':
      return 'Plantilla';

    case 'REACTION':
      return 'Reacción';

    default:
      return 'Mensaje Desconocido';
  }
}

const getLastMessageDisplay = (lastMessage: any) => {
  if (lastMessage.conversation) {
    return String(lastMessage.conversation);
  }

  if (typeof lastMessage.text === 'string') {
    return String(lastMessage.text);
  }

  return lastMessage?.text?.body || '';
};

const formatConversationIdentifier = (
  identifier: string,
  service: ChatServiceType
) => {
  if (service === 'WHATSAPP_WEB' || service === 'WHATSAPP_API') {
    const deviceIdentifier = identifier.replace(/\s|\+/g, '');
    if (!isValidDisplayIdentifierOrPhone(deviceIdentifier)) {
      return 'Usuario desconocido';
    }
    return formatPhoneNumber(`+${deviceIdentifier}`);
  }

  if (service === 'FB_MESSENGER' || service === 'INSTAGRAM') {
    return `@${identifier}`;
  }

  return 'Desconocido';
};

function ConversationPreview(props: Props) {
  const {
    current,
    conversation,
    selected,
    currentDevice,
    onClick,
    onToggleSelect,
  } = props;

  const {lastMessage, read, pinned, requiresAssistance} = conversation;

  const {togglePinConversation, closeConversation} = useConversationActions(
    conversation.id
  );

  const [moreOptionsEl, setMoreOptionsEl] = useState(null);
  const moreOptionsOpen = Boolean(moreOptionsEl);

  const tags = conversation.tags ? conversation.tags.map(tag => tag.tag) : [];

  const time = timeAgo(conversation.lastMessageAt);

  const contactName = getConversationDisplayName(conversation);
  const unread = !read;

  const classes = useStyles({current})();

  const handleClicked = () => {
    onClick(conversation);
  };

  const serviceIcon = getMultichatServiceIcon(conversation.ravenService);

  const isArchivedConversation =
    conversation.deviceId !== currentDevice?.id &&
    conversation.ravenService === 'WHATSAPP_WEB';
  const conversationIdentifier = conversation.identifier
    ? conversation.identifier
    : conversation.deviceIdentifier;

  const handleMoreOptionsOpen = event => {
    event.stopPropagation();
    setMoreOptionsEl(event.currentTarget);
  };

  const handleMoreOptionsClose = () => {
    setMoreOptionsEl(null);
  };

  return (
    <Button className={classes.previewWrap} onClick={handleClicked}>
      <TiendadaTooltip title="Más opciones" placement="top">
        <IconButton
          className={classes.moreOptionsButton}
          onClick={handleMoreOptionsOpen}
          size="small"
        >
          <ExpandMore />
        </IconButton>
      </TiendadaTooltip>

      <Popover
        open={moreOptionsOpen}
        anchorEl={moreOptionsEl}
        onClose={handleMoreOptionsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{
          paper: classes.popoverContent,
        }}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <CustomButton
          startIcon={<PinIcon />}
          onClick={() => {
            handleMoreOptionsClose();
            togglePinConversation(!pinned);
          }}
        >
          {pinned ? 'Desanclar conversación' : 'Fijar conversación'}
        </CustomButton>
        <CustomButton
          startIcon={<Close />}
          onClick={() => {
            closeConversation();
            handleMoreOptionsClose();
          }}
        >
          Cerrar conversación
        </CustomButton>
      </Popover>

      <div className={classes.preview} data-checked={!!selected}>
        <span
          className={classes.checkboxContainer}
          data-checkbox
          onClick={e => {
            e.stopPropagation();
          }}
          data-checked={!!selected}
        >
          <Checkbox
            color="primary"
            checked={selected}
            className={classes.checkbox}
            onChange={e => {
              onToggleSelect(conversation);
            }}
          />
        </span>
        <div className={classes.content} data-unread={unread}>
          <div className={classes.header}>
            <div className={classes.contactInfo}>
              {pinned && (
                <span className={classes.icon}>
                  <PinIcon />
                </span>
              )}
              <Typography className={classes.contactName}>
                {contactName}
              </Typography>
            </div>
            <div className={classes.conversationInfo}>
              <Typography className={classes.time}>{time}</Typography>

              {isArchivedConversation && (
                <Tooltip
                  placement="right"
                  enterTouchDelay={0}
                  title={'Archivado'}
                >
                  <Warning
                    className={`${
                      time
                        ? classes.conversationState
                        : classes.conversationStateBulled
                    }`}
                  />
                </Tooltip>
              )}
            </div>
          </div>
          <div className={classes.messageContent}>
            {lastMessage && (
              <LastMessagePreview
                conversation={conversation}
                classes={classes}
              />
            )}
            <div className={classes.tagList}>
              {tags.map(tag => (
                <span key={tag} className={classes.tag}>
                  {tag}
                </span>
              ))}
            </div>
            {unread && <span className={classes.unreadBulled}></span>}
          </div>

          <div className={classes.conversationFooter}>
            <div className={classes.serviceLabel}>
              <picture className={classes.previewServiceIcon}>
                <img src={serviceIcon} alt={conversation.ravenService} />
              </picture>
              <Typography className={classes.textTooltip}>
                {formatConversationIdentifier(
                  conversationIdentifier,
                  conversation.ravenService
                )}
              </Typography>
            </div>

            {requiresAssistance && (
              <span className={classes.requiresAssistance}>
                <EmergencyIcon />
                <span>Ayuda</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </Button>
  );
}

// $FlowFixMe
const ConversationPreviewMemo = React.memo(ConversationPreview);

export default ConversationPreviewMemo;

type LastMessagePreviewProps = {
  conversation: Conversation,
  classes: {
    [key: string]: string,
  },
};

function LastMessagePreview(props: LastMessagePreviewProps) {
  const {conversation, classes} = props;

  const {lastMessageType, lastMessage} = conversation;
  if (!lastMessage) return null;

  if (lastMessageType === 'TEXT') {
    return (
      <p className={classes.message}>{getLastMessageDisplay(lastMessage)}</p>
    );
  }

  if (lastMessageType === 'REACTION' && lastMessage.reactionMessage) {
    const {reactionMessage} = lastMessage;
    const reaction = reactionMessage.text || '';
    // $FlowFixMe
    const fromMe = reactionMessage?.key?.fromMe || false;

    const message = fromMe
      ? // $FlowFixMe
        `Reaccionaste con ${reaction}`
      : // $FlowFixMe
        `Reaccionó con ${reaction}`;
    return (
      <p className={classes.message}>
        <div className={classes.messageMultimedia}>
          <span className={classes.messageMultimediaLabel}>
            <span className={classes.textTooltip}>{message}</span>
          </span>
        </div>
      </p>
    );
  }

  return (
    <p className={classes.message}>
      <div className={classes.messageMultimedia}>
        <span className={classes.messageIcon}>
          {lastMessageType && (
            <FileTypeIcons type={lastMessageType.toLocaleLowerCase()} />
          )}
        </span>
        <span className={classes.messageMultimediaLabel}>
          {getLabelByMessageType(lastMessageType)}
        </span>
      </div>
    </p>
  );
}
